import { MAX_UPLOAD_BYTES } from '@batiflow/contracts';
import { isolate } from '@batiflow/ui';
import { Button, __css, tokens } from '@fluentui/react-components';
import {
  ArrowUpload24Regular,
  DocumentAdd20Regular,
  FolderAdd20Regular,
} from '@fluentui/react-icons';
import { useId, useRef, useState, type ChangeEvent, type DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocale } from '@/app/providers/i18n-provider';
import { useOrganizationWritable } from '@/features/billing';
import {
  collectDroppedFiles,
  formatBytes,
  isActive,
  isJunkFile,
  precheck,
  type UploadTarget,
} from './upload-model';
import { UploadMetadataDialog, type NamingContext } from './upload-metadata-dialog';
import { UploadRows } from './upload-rows';
import type { Uploads } from './use-uploads';

const DASH = tokens.colorNeutralStroke1;
const dashed = (color: string) => ({
  borderBlockStartWidth: tokens.strokeWidthThick,
  borderBlockEndWidth: tokens.strokeWidthThick,
  borderInlineStartWidth: tokens.strokeWidthThick,
  borderInlineEndWidth: tokens.strokeWidthThick,
  borderBlockStartStyle: 'dashed' as const,
  borderBlockEndStyle: 'dashed' as const,
  borderInlineStartStyle: 'dashed' as const,
  borderInlineEndStyle: 'dashed' as const,
  borderBlockStartColor: color,
  borderBlockEndColor: color,
  borderInlineStartColor: color,
  borderInlineEndColor: color,
});

const useStyles = __css({"root":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1noh358"},"zone":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Bt984gj":"f122n59","Belr9w4":"f1wn3y0l","Dbcxam":0,"rjzwhg":0,"Bblux5w":"f16epzkf","Bnt446c":0,"B1hrpyx":0,"Fjva8n":"fjshy37","fsow6f":"f17mccla","Beyfa6y":0,"Bbmb7ep":0,"Btl43ni":0,"B7oj6ja":0,"Dimara":"f1o0qvyv","De3pzq":"fxugw4r","gggo0z":"fh5srev","Behc1zw":"f1pl75eu","B4m11q7":"f9sfs53","tzxy30":"f1k4bv2a","B834pou":"fysy8rd","uk9mye":"fb3g746","rm1204":"f6yh0zl","Bwezv66":"fbvzph5","F1poyb":"f1nxen24","mbw9mh":"f1uccd92","ijz38e":"f32ghg3","Ctt3hk":"f129z43g"},"zoneActive":{"De3pzq":"f16xkysk","gggo0z":"fh5srev","Behc1zw":"f1pl75eu","B4m11q7":"f9sfs53","tzxy30":"f1k4bv2a","B834pou":"fysy8rd","uk9mye":"fb3g746","rm1204":"f6yh0zl","Bwezv66":"fbvzph5","F1poyb":"fo0u7mh","mbw9mh":"fh32zs","ijz38e":"fyvlgxw","Ctt3hk":"f9srtw2"},"title":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","Be2twd7":"fod5ikn","Bg96gwp":"faaz57k","Bhrd7zp":"fl43uef"},"hint":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","D305fb":"f1qaqxsn","Be2twd7":"fy9rknc","Bg96gwp":"fwrc4pm","sj55zd":"fkfq4zb"},"buttons":{"mc9l5x":"f22iagw","Eh141a":"fni485r","Brf1p80":"f4d9j23","i8kkvl":"fsnqrgy","Belr9w4":"f1wn3y0l"},"others":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","Be2twd7":"fy9rknc","sj55zd":"fkfq4zb"}});

export interface UploadZoneProps {
  uploads: Uploads;
  /** The open folder: the space and the folder the files go into. */
  target: UploadTarget;
  /** What the metadata form needs: the project's code and the names of the folder and above. */
  naming: NamingContext;
}

const hasFiles = (event: DragEvent): boolean => event.dataTransfer.types.includes('Files');

/** Files waiting for their metadata: one batch per drop or pick. */
interface Batch {
  id: number;
  files: readonly File[];
  target: UploadTarget;
}

/**
 * Adds files to the open folder: drop files or folders on the zone, or use the buttons (the
 * keyboard way). A dropped folder is read down to its files, which all go into the open folder.
 * Files that can be sent first go through a short metadata form (or "Skip"); a file refused
 * at once (type, size) shows its line without asking anything.
 * Each file gets a line (`UploadRows`) with its progress and, when it ends, its result in words.
 */
export function UploadZone({ uploads, target, naming }: UploadZoneProps) {
  const { t } = useTranslation('documents');
  const styles = useStyles();
  const locale = useLocale();
  const { controller, rows } = uploads;
  // A read-only or suspended office refuses every upload (403 ORGANIZATION_READ_ONLY): say so up front.
  const { writable, explanation } = useOrganizationWritable();
  const lockedNoteId = useId();
  const filesInput = useRef<HTMLInputElement>(null);
  const folderInput = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [queue, setQueue] = useState<readonly Batch[]>([]);
  const batchCount = useRef(0);

  const here = rows.filter((row) => row.folderId === target.folderId);
  const elsewhere = rows.filter((row) => row.folderId !== target.folderId && isActive(row)).length;

  const add = (files: readonly File[]) => {
    const wanted = files.filter((file) => !isJunkFile(file.name));
    // A file refused at once has nothing to describe: its line explains the refusal.
    const refused = wanted.filter((file) => precheck(file) !== null);
    const sendable = wanted.filter((file) => precheck(file) === null);
    if (refused.length > 0) controller.addFiles(refused, target);
    if (sendable.length > 0) {
      batchCount.current += 1;
      const queued: Batch = { id: batchCount.current, files: sendable, target };
      setQueue((current) => [...current, queued]);
    }
  };
  const batch = queue[0];
  const closeBatch = () => {
    setQueue((current) => current.slice(1));
  };
  const onPick = (event: ChangeEvent<HTMLInputElement>) => {
    add(Array.from(event.target.files ?? []));
    // The same file may be chosen again later.
    event.target.value = '';
  };
  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    if (!hasFiles(event)) return;
    event.preventDefault();
    setDragging(false);
    if (!writable) return;
    void collectDroppedFiles(event.dataTransfer).then(add);
  };

  return (
    <section className={styles.root} aria-label={t('upload.region')}>
      <div
        className={`${styles.zone} ${dragging ? styles.zoneActive : ''}`}
        onDragOver={(event) => {
          if (!hasFiles(event)) return;
          event.preventDefault();
          setDragging(writable);
        }}
        onDragLeave={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDragging(false);
        }}
        onDrop={onDrop}
      >
        <ArrowUpload24Regular aria-hidden="true" />
        <p className={styles.title}>
          {dragging ? t('upload.zone.dropNow') : t('upload.zone.title')}
        </p>
        <p className={styles.hint}>
          {t('upload.zone.hint', { max: isolate(formatBytes(MAX_UPLOAD_BYTES, locale)) })}
        </p>
        {explanation ? (
          <p id={lockedNoteId} className={styles.hint}>
            {explanation}
          </p>
        ) : null}
        <div className={styles.buttons}>
          <Button
            appearance="primary"
            icon={<DocumentAdd20Regular />}
            disabledFocusable={!writable}
            {...(writable ? {} : { 'aria-describedby': lockedNoteId })}
            onClick={() => {
              filesInput.current?.click();
            }}
          >
            {t('upload.zone.addFiles')}
          </Button>
          <Button
            icon={<FolderAdd20Regular />}
            disabledFocusable={!writable}
            {...(writable ? {} : { 'aria-describedby': lockedNoteId })}
            onClick={() => {
              folderInput.current?.click();
            }}
          >
            {t('upload.zone.addFolder')}
          </Button>
        </div>
        <input ref={filesInput} type="file" multiple hidden onChange={onPick} />
        <input
          ref={folderInput}
          type="file"
          hidden
          onChange={onPick}
          // Not in React's types; every current browser reads a whole folder with it.
          {...{ webkitdirectory: '' }}
        />
      </div>
      <UploadRows rows={here} controller={controller} />
      {elsewhere > 0 ? (
        <p className={styles.others} role="status">
          {t('upload.elsewhere', { count: elsewhere })}
        </p>
      ) : null}
      {batch ? (
        <UploadMetadataDialog
          // One dialog per batch: its fields start again from the folder's suggestions.
          key={batch.id}
          files={batch.files}
          naming={naming}
          onConfirm={(extras) => {
            controller.addFiles(batch.files, batch.target, extras);
            closeBatch();
          }}
          onSkip={() => {
            controller.addFiles(batch.files, batch.target);
            closeBatch();
          }}
          onCancel={closeBatch}
        />
      ) : null}
    </section>
  );
}


import "/opt/batiflow/app/apps/web/src/features/documents/upload-zone.griffel.css";